/**
 * Quality ladder and user preferences. Runtime-cheap settings form an ordered ladder that the
 * adaptive governor moves along; geometry detail is chosen once per load (changing it applies
 * on the next entry) so the scene never visibly rebuilds while being watched.
 *
 * Order of reductions (cheapest visual loss first): render scale and pixel ratio, then
 * volumetric light, SSAO, shell counts, shadow softness/resolution, particles. The background blur
 * stays (the owner wants the far plane soft): lighter at the low level, off only at the last
 * resort, minimal.
 * Population and collision correctness are never part of the ladder.
 */

export interface QualityLevel {
  index: number;
  name: string;
  renderScale: number;
  pixelRatioCap: number;
  taa: boolean;
  ssao: boolean;
  dof: boolean;
  volumetric: boolean;
  volumetricSteps: number;
  bloom: boolean;
  shadowResolution: 1024 | 2048 | 4096;
  shadowSoftness: 'pcf3' | 'pcf5' | 'pcss';
  furShells: number;
  mossShells: number;
  /** Decorative particle budget multiplier (0..1). */
  particles: number;
  /** Depth-of-field strength when `dof` is on (1 full … 0.5 subtle). */
  dofStrength: number;
  /**
   * A lighter depth of field: the same blur from fewer samples per pixel (about half), for the
   * lowest levels. Only the sample pattern changes, so moving to or from it never rebuilds the
   * post stack and the blur does not visibly change.
   */
  dofLite: boolean;
}

export const QUALITY_LADDER: readonly QualityLevel[] = (
  [
    { index: 0, name: 'minimal', renderScale: 0.7, pixelRatioCap: 1, taa: false, ssao: false, dof: false, volumetric: false, volumetricSteps: 0, bloom: false, shadowResolution: 1024, shadowSoftness: 'pcf3', furShells: 4, mossShells: 3, particles: 0.4 },
    { index: 1, name: 'low', renderScale: 0.85, pixelRatioCap: 1, taa: true, ssao: false, dof: true, volumetric: false, volumetricSteps: 0, bloom: true, shadowResolution: 1024, shadowSoftness: 'pcf3', furShells: 5, mossShells: 4, particles: 0.6 },
    { index: 2, name: 'medium-low', renderScale: 1, pixelRatioCap: 1, taa: true, ssao: false, dof: true, volumetric: false, volumetricSteps: 0, bloom: true, shadowResolution: 1024, shadowSoftness: 'pcf5', furShells: 6, mossShells: 6, particles: 0.75 },
    { index: 3, name: 'medium', renderScale: 1, pixelRatioCap: 1.25, taa: true, ssao: true, dof: true, volumetric: true, volumetricSteps: 14, bloom: true, shadowResolution: 2048, shadowSoftness: 'pcf5', furShells: 8, mossShells: 8, particles: 1 },
    { index: 4, name: 'high', renderScale: 1, pixelRatioCap: 1.5, taa: true, ssao: true, dof: true, volumetric: true, volumetricSteps: 22, bloom: true, shadowResolution: 2048, shadowSoftness: 'pcf5', furShells: 10, mossShells: 10, particles: 1 },
    { index: 5, name: 'highest', renderScale: 1, pixelRatioCap: 2, taa: true, ssao: true, dof: true, volumetric: true, volumetricSteps: 32, bloom: true, shadowResolution: 2048, shadowSoftness: 'pcss', furShells: 12, mossShells: 14, particles: 1 },
    // For strong GPUs: sharper and softer shadows, finer light shafts, deeper fur and moss.
    { index: 6, name: 'ultra', renderScale: 1, pixelRatioCap: 2, taa: true, ssao: true, dof: true, volumetric: true, volumetricSteps: 40, bloom: true, shadowResolution: 4096, shadowSoftness: 'pcss', furShells: 16, mossShells: 18, particles: 1 },
  ] satisfies Array<Omit<QualityLevel, 'dofStrength' | 'dofLite'>>
  // The two lowest levels blur with the lighter sample pattern (minimal only when the blur is
  // pinned on by hand).
).map((q) => ({ ...q, dofStrength: 1, dofLite: q.index <= 1 }));

export type PerformancePreference = 'battery' | 'balanced' | 'high';
export type DetailSetting = 'auto' | 'low' | 'medium' | 'high' | 'ultra';
export type DensitySetting = 'low' | 'normal' | 'lush';

export interface PreferenceProfile {
  frameCap: 30 | 60;
  minLevel: number;
  maxLevel: number;
  startLevel: number;
  geometry: 'low' | 'medium' | 'high';
}

export const PREFERENCE_PROFILES: Record<PerformancePreference, PreferenceProfile> = {
  battery: { frameCap: 30, minLevel: 0, maxLevel: 2, startLevel: 1, geometry: 'low' },
  balanced: { frameCap: 60, minLevel: 0, maxLevel: 4, startLevel: 2, geometry: 'medium' },
  // May climb to Ultra on a device that holds its frame rate there (the governor probes and
  // remembers a level that failed).
  high: { frameCap: 60, minLevel: 1, maxLevel: 6, startLevel: 3, geometry: 'high' },
};

/** Fixed (non-automatic) visual detail choices map to a ladder level and geometry detail. */
export const MANUAL_DETAIL: Record<Exclude<DetailSetting, 'auto'>, { level: number; geometry: 'low' | 'medium' | 'high' }> = {
  low: { level: 1, geometry: 'low' },
  medium: { level: 3, geometry: 'medium' },
  high: { level: 5, geometry: 'high' },
  ultra: { level: 6, geometry: 'high' },
};

export function resolveGeometry(pref: PerformancePreference, detail: DetailSetting): 'low' | 'medium' | 'high' {
  return detail === 'auto' ? PREFERENCE_PROFILES[pref].geometry : MANUAL_DETAIL[detail].geometry;
}

// --- Hand-set graphics options ---------------------------------------------------------------

/**
 * Graphics options a viewer can pin by hand. Each is 'auto' (the ladder level decides, and the
 * governor may move it) or a fixed choice that holds whatever the governor does; the governor
 * keeps adapting everything left on automatic.
 */
export interface GraphicsOverrides {
  /** Backing-store resolution as a percentage of the display's own (native = 100; > 100 supersamples). */
  resolution: 'auto' | '50' | '75' | '100' | '125' | '150' | '200';
  /** Frame-rate cap: 30, 60, or the display's own refresh rate. */
  frameRate: 'auto' | '30' | '60' | 'max';
  shadows: 'auto' | 'low' | 'medium' | 'high' | 'ultra';
  lightShafts: 'auto' | 'off' | 'low' | 'medium' | 'high';
  ambientOcclusion: 'auto' | 'off' | 'on';
  depthOfField: 'auto' | 'off' | 'subtle' | 'full';
  bloom: 'auto' | 'off' | 'on';
  /** Temporal anti-aliasing ("motion smoothing"): steadies fine moss, fur and leaf edges. */
  smoothing: 'auto' | 'off' | 'on';
  furMoss: 'auto' | 'low' | 'medium' | 'high' | 'ultra';
}

export const AUTO_GRAPHICS: GraphicsOverrides = {
  resolution: 'auto',
  frameRate: 'auto',
  shadows: 'auto',
  lightShafts: 'auto',
  ambientOcclusion: 'auto',
  depthOfField: 'auto',
  bloom: 'auto',
  smoothing: 'auto',
  furMoss: 'auto',
};

const SHADOW_CHOICES = {
  low: { shadowResolution: 1024, shadowSoftness: 'pcf3' },
  medium: { shadowResolution: 2048, shadowSoftness: 'pcf5' },
  high: { shadowResolution: 2048, shadowSoftness: 'pcss' },
  ultra: { shadowResolution: 4096, shadowSoftness: 'pcss' },
} as const;
const SHAFT_STEPS = { low: 14, medium: 22, high: 40 } as const;
const SHELLS = { low: { furShells: 5, mossShells: 4 }, medium: { furShells: 8, mossShells: 8 }, high: { furShells: 12, mossShells: 14 }, ultra: { furShells: 16, mossShells: 18 } } as const;

/** No cap on the display's pixel ratio (a pinned resolution is relative to the native one). */
const NO_RATIO_CAP = 8;

/** The level actually rendered: the ladder level with any hand-set options laid over it. */
export function applyOverrides(base: QualityLevel, o: GraphicsOverrides): QualityLevel {
  const q: QualityLevel = { ...base };
  if (o.resolution !== 'auto') {
    q.pixelRatioCap = NO_RATIO_CAP;
    q.renderScale = Number(o.resolution) / 100;
  }
  if (o.shadows !== 'auto') Object.assign(q, SHADOW_CHOICES[o.shadows]);
  if (o.lightShafts === 'off') q.volumetric = false;
  else if (o.lightShafts !== 'auto') {
    q.volumetric = true;
    q.volumetricSteps = SHAFT_STEPS[o.lightShafts];
  }
  if (o.ambientOcclusion !== 'auto') q.ssao = o.ambientOcclusion === 'on';
  if (o.depthOfField === 'off') q.dof = false;
  else if (o.depthOfField !== 'auto') {
    q.dof = true;
    q.dofStrength = o.depthOfField === 'subtle' ? 0.5 : 1;
  }
  if (o.bloom !== 'auto') q.bloom = o.bloom === 'on';
  if (o.smoothing !== 'auto') q.taa = o.smoothing === 'on';
  if (o.furMoss !== 'auto') Object.assign(q, SHELLS[o.furMoss]);
  return q;
}

/** Frame-rate cap for the renderer: a pinned choice, else the preference's (null = display rate). */
export function resolveFrameCap(pref: PerformancePreference, o: GraphicsOverrides): 30 | 60 | null {
  if (o.frameRate === '30') return 30;
  if (o.frameRate === '60') return 60;
  if (o.frameRate === 'max') return null;
  return PREFERENCE_PROFILES[pref].frameCap === 30 ? 30 : null;
}

/** Whether any option is hand-set. */
export function hasOverrides(o: GraphicsOverrides): boolean {
  return (Object.keys(AUTO_GRAPHICS) as Array<keyof GraphicsOverrides>).some((k) => o[k] !== 'auto');
}
